import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useProfile, useUpdateProfile, useUpdateBillingProfile, useProfileBilling } from '@/shared/hooks/useCloudData';
import { Language } from '@/shared/types';
import { toast } from 'sonner';
import { useSubscription, TIERS } from '@/features/subscription/hooks/useSubscription';
import { PlanCards } from '@/features/subscription/components/PlanCards';
import { ROIBanner } from '@/features/subscription/components/ROIBanner';
import { PricingFAQ } from '@/features/subscription/components/PricingFAQ';
import { useStudioSettings, ChecklistItem, STUDIO_DEFAULTS } from '../hooks/useStudioSettings';

const DEFAULT_CHECKLIST_ITEMS: ChecklistItem[] = [
  { id: 'default-1', label: 'Contre-indications vérifiées',     required: true },
  { id: 'default-2', label: 'Allergies vérifiées',              required: true },
  { id: 'default-3', label: 'Traitements en cours vérifiés',    required: true },
  { id: 'default-4', label: 'Risques expliqués au client',      required: true },
  { id: 'default-5', label: 'Soins post-acte expliqués',        required: true },
  { id: 'default-6', label: 'Aiguilles neuves confirmées',      required: true },
  { id: 'default-7', label: 'Espace de travail désinfecté',     required: true },
  { id: 'default-8', label: 'Gants portés',                     required: true },
];
import {
  useServiceCatalog, useSaveServiceCatalogItem, useDeleteServiceCatalogItem,
} from '@/features/quotes/hooks/useServiceCatalog';
import { PlanGate } from '@/shared/components/PlanGate';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Save, CreditCard, Upload, X, Plus, Trash2,
  Check, Pencil, Loader2, GripVertical,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── helpers ──────────────────────────────────────────────────────────────────

const INVOICE_COLORS = ['#2eb8aa', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];
const VAT_RATES = [0, 5.5, 10, 20];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">{children}</h3>;
}

function SaveBar({ onSave, saving }: { onSave: () => void; saving: boolean }) {
  return (
    <div className="pt-4">
      <Button onClick={onSave} disabled={saving} className="w-full gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
    </div>
  );
}

// ── Sortable checklist item ───────────────────────────────────────────────────

function SortableCheckItem({ item, onRemove }: { item: ChecklistItem; onRemove: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-2 p-2.5 rounded-xl bg-secondary/40"
    >
      <button {...attributes} {...listeners} className="cursor-grab text-muted-foreground hover:text-foreground touch-none shrink-0">
        <GripVertical size={14} />
      </button>
      <span className="flex-1 text-sm">{item.label}</span>
      <button onClick={() => onRemove(item.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const navigate    = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, setLanguage } = useLanguage();
  const { isAdmin, signOut } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const { tier, subscriptionEnd, trialEnd, subscriptionStatus, billingInterval, isLegacyFree, manageSubscription, startCheckout } = useSubscription();
  const updateProfile  = useUpdateProfile();
  const updateBilling  = useUpdateBillingProfile();
  const { data: billing } = useProfileBilling();
  const { settings: studioSettings, save: saveStudio } = useStudioSettings();
  const { data: catalog = [] } = useServiceCatalog();
  const saveItem   = useSaveServiceCatalogItem();
  const deleteItem = useDeleteServiceCatalogItem();

  // ── Onglet 1 — Profil ──────────────────────────────────────────────────
  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]    = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email,       setEmail]       = useState('');
  const [bizName,     setBizName]     = useState('');
  const [instagram,   setInstagram]   = useState('');
  const [phone,       setPhone]       = useState('');
  const [addrStreet,  setAddrStreet]  = useState('');
  const [addrZip,     setAddrZip]     = useState('');
  const [addrCity,    setAddrCity]    = useState('');
  const [defLang,     setDefLang]     = useState<Language>('fr');

  // ── Onglet 2 — Facturation ─────────────────────────────────────────────
  const [siret,        setSiret]        = useState('');
  const [vatNumber,    setVatNumber]    = useState('');
  const [billStreet,   setBillStreet]   = useState('');
  const [billZip,      setBillZip]      = useState('');
  const [billCity,     setBillCity]     = useState('');
  const [logoUrl,      setLogoUrl]      = useState<string | null>(null);
  const [invColor,     setInvColor]     = useState('#2eb8aa');
  const [hexInput,     setHexInput]     = useState('#2eb8aa');
  const [invPrefix,    setInvPrefix]    = useState('FAC');
  const [quotePrefix,  setQuotePrefix]  = useState('DEV');
  const [payTerms,     setPayTerms]     = useState('');
  const [invFooter,    setInvFooter]    = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);

  // ── Onglet 3 — Studio ──────────────────────────────────────────────────
  const [consentMonths, setConsentMonths] = useState(12);
  const [pauseDays,     setPauseDays]     = useState(3);
  const [stockThresh,   setStockThresh]   = useState(5);
  const [checklist,     setChecklist]     = useState<ChecklistItem[]>([]);
  const [newCheckLabel, setNewCheckLabel] = useState('');
  // Catalog inline edit
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', default_price: 0, vat_rate: 0 });
  const [newItem, setNewItem]   = useState({ name: '', description: '', default_price: 0, vat_rate: 0, adding: false });

  // ── Onglet 4 — Sécurité ────────────────────────────────────────────────
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pinCode,    setPinCode]    = useState('1234');

  // ── Suppression de compte (RGPD) ──────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting,      setDeleting]      = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'SUPPRIMER') return;
    setDeleting(true);
    try {
      const { data: { session: currentSession } } = await (supabase as any).auth.getSession();
      const { error } = await (supabase as any).functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${currentSession?.access_token}` },
      });
      if (error) throw error;
      toast.success('Compte supprimé. Au revoir.');
      await signOut();
      navigate('/');
    } catch {
      toast.error('Erreur lors de la suppression. Contactez le support.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Seed from profile ──────────────────────────────────────────────────
  // Onglet 1 + sécurité — depuis Dexie via useProfile
  useEffect(() => {
    if (!profile) return;
    const p = profile as any;
    setFirstName(p.first_name   || '');
    setLastName(p.last_name     || '');
    setDisplayName(p.display_name || '');
    setEmail(p.email            || '');
    setBizName(p.business_name  || '');
    setInstagram(p.instagram    || '');
    setPhone(p.phone            || '');
    const ba = (p.business_address as Record<string, string>) || {};
    setAddrStreet(ba.street || '');
    setAddrZip(ba.postal_code || '');
    setAddrCity(ba.city || '');
    setDefLang((p.default_language || 'fr') as Language);
    setLogoUrl(p.logo_url       || null);
    setPinEnabled(p.pin_enabled    || false);
    setPinCode(p.pin_code          || '1234');
  }, [profile]);

  // Onglet 2 — depuis Supabase direct via useProfileBilling
  useEffect(() => {
    if (!billing) return;
    setSiret(billing.siret);
    setVatNumber(billing.vat_number);
    const bill = billing.billing_address || {};
    setBillStreet(bill.street || '');
    setBillZip(bill.postal_code || '');
    setBillCity(bill.city || '');
    setInvColor(billing.invoice_color || '#2eb8aa');
    setHexInput(billing.invoice_color || '#2eb8aa');
    setInvPrefix(billing.invoice_prefix || 'FAC');
    setQuotePrefix(billing.quote_prefix || 'DEV');
    setPayTerms(billing.payment_terms || '');
    setInvFooter(billing.invoice_footer || '');
  }, [billing]);

  useEffect(() => {
    if (studioSettings === STUDIO_DEFAULTS) return; // query not yet resolved
    setConsentMonths(studioSettings.consent_validity_months);
    setPauseDays(studioSettings.pause_alert_days);
    setStockThresh(studioSettings.stock_low_threshold);

    if (studioSettings.checklist_items.length === 0) {
      // First load — seed defaults locally and persist to DB
      setChecklist(DEFAULT_CHECKLIST_ITEMS);
      saveStudio.mutate({ checklist_items: DEFAULT_CHECKLIST_ITEMS });
    } else {
      setChecklist(studioSettings.checklist_items);
    }
  }, [studioSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Logo upload (compress to base64) ──────────────────────────────────
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image trop lourde (max 2 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 200;
        const scale = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width  = img.width  * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        setLogoUrl(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  // ── Save handlers ──────────────────────────────────────────────────────
  const handleSaveProfil = () => {
    updateProfile.mutate({
      first_name:  firstName,
      last_name:   lastName,
      display_name: displayName,
      email,
      business_name: bizName,
      instagram,
      phone,
      business_address: { street: addrStreet, postal_code: addrZip, city: addrCity },
      default_language: defLang,
    }, {
      onSuccess: () => { setLanguage(defLang); toast.success('Profil enregistré'); },
    });
  };

  const handleSaveFacturation = () => {
    updateBilling.mutate({
      siret,
      vat_number:      vatNumber,
      billing_address: { street: billStreet, postal_code: billZip, city: billCity },
      invoice_color:   invColor,
      invoice_prefix:  invPrefix,
      quote_prefix:    quotePrefix,
      payment_terms:   payTerms,
      invoice_footer:  invFooter,
    }, {
      onSuccess: () => toast.success('Facturation enregistrée'),
    });
    // Logo goes through profile (existing sync path)
    updateProfile.mutate({ logo_url: logoUrl ?? undefined } as any);
  };

  const handleSaveStudio = () => {
    saveStudio.mutate({
      consent_validity_months: consentMonths,
      pause_alert_days:        pauseDays,
      stock_low_threshold:     stockThresh,
      checklist_items:         checklist,
    }, {
      onSuccess: () => toast.success('Studio enregistré'),
    });
  };

  const handleSaveSecurite = () => {
    updateProfile.mutate({ pin_enabled: pinEnabled, pin_code: pinCode }, {
      onSuccess: () => toast.success('Sécurité enregistrée'),
    });
  };

  // ── Checklist drag & drop ──────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleChecklistDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setChecklist(items => {
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      const next = arrayMove(items, oldIndex, newIndex);
      saveStudio.mutate({ checklist_items: next });
      return next;
    });
  };

  // ── Checklist helpers ──────────────────────────────────────────────────
  const addCheckItem = () => {
    const label = newCheckLabel.trim();
    if (!label) return;
    setChecklist(c => [...c, { id: crypto.randomUUID(), label, required: false }]);
    setNewCheckLabel('');
  };
  const removeCheckItem = (id: string) => setChecklist(c => c.filter(i => i.id !== id));

  // ── Catalog inline edit helpers ────────────────────────────────────────
  const startEdit = useCallback((item: typeof catalog[0]) => {
    setEditingItemId(item.id);
    setEditForm({ name: item.name, description: item.description, default_price: item.default_price, vat_rate: item.vat_rate });
  }, []);

  const commitEdit = () => {
    if (!editingItemId || !editForm.name.trim()) return;
    saveItem.mutate({ id: editingItemId, ...editForm }, {
      onSuccess: () => { setEditingItemId(null); toast.success('Prestation mise à jour'); },
    });
  };

  const commitNew = () => {
    if (!newItem.name.trim()) return;
    saveItem.mutate({ name: newItem.name, description: newItem.description, default_price: newItem.default_price, vat_rate: newItem.vat_rate }, {
      onSuccess: () => {
        setNewItem({ name: '', description: '', default_price: 0, vat_rate: 0, adding: false });
        toast.success('Prestation ajoutée');
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const languages: { value: Language; label: string }[] = [
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'en', label: '🇬🇧 English' },
    { value: 'es', label: '🇪🇸 Español' },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pt-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold font-display">Réglages</h1>
      </div>

      <Tabs value={searchParams.get('tab') ?? 'profil'} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto">
          <TabsTrigger value="profil"       className="text-xs py-2">Profil</TabsTrigger>
          <TabsTrigger value="facturation"  className="text-xs py-2">Facturation</TabsTrigger>
          <TabsTrigger value="studio"       className="text-xs py-2">Studio</TabsTrigger>
          <TabsTrigger value="securite"     className="text-xs py-2">Sécurité</TabsTrigger>
          <TabsTrigger value="abonnement"   className="text-xs py-2">Abonnement</TabsTrigger>
        </TabsList>

        {/* ── ONGLET 1 — PROFIL ─────────────────────────────────────────── */}
        <TabsContent value="profil" className="space-y-4 pt-4">
          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Identité</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom">
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jean" />
              </Field>
              <Field label="Nom">
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Dupont" />
              </Field>
            </div>
            <Field label="Nom affiché (praticien)">
              <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Jean Dupont" />
            </Field>
            <Field label="Email">
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </Field>
            <Field label="Nom de l'établissement">
              <Input value={bizName} onChange={e => setBizName(e.target.value)} placeholder="Studio Ink" />
            </Field>
          </div>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Contact pro</SectionTitle>
            <Field label="Téléphone">
              <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+33 6 00 00 00 00" />
            </Field>
            <Field label="Instagram">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                <Input value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="handle" className="pl-7" />
              </div>
            </Field>
          </div>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Adresse professionnelle</SectionTitle>
            <Field label="Rue">
              <Input value={addrStreet} onChange={e => setAddrStreet(e.target.value)} placeholder="12 rue de la Paix" />
            </Field>
            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <Field label="Code postal">
                <Input value={addrZip} onChange={e => setAddrZip(e.target.value)} placeholder="75001" />
              </Field>
              <Field label="Ville">
                <Input value={addrCity} onChange={e => setAddrCity(e.target.value)} placeholder="Paris" />
              </Field>
            </div>
          </div>

          <div className="glass-card p-5 space-y-3">
            <SectionTitle>Langue par défaut</SectionTitle>
            <div className="flex gap-2">
              {languages.map(l => (
                <button key={l.value} onClick={() => setDefLang(l.value)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${defLang === l.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <SaveBar onSave={handleSaveProfil} saving={updateProfile.isPending} />
        </TabsContent>

        {/* ── ONGLET 2 — FACTURATION ────────────────────────────────────── */}
        <TabsContent value="facturation" className="space-y-4 pt-4">
          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Identité fiscale</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="SIRET">
                <Input value={siret} onChange={e => setSiret(e.target.value)} placeholder="123 456 789 00012" />
              </Field>
              <Field label="N° TVA intracommunautaire">
                <Input value={vatNumber} onChange={e => setVatNumber(e.target.value)} placeholder="FR 12 345678901" />
              </Field>
            </div>
          </div>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Adresse de facturation</SectionTitle>
            <Field label="Rue">
              <Input value={billStreet} onChange={e => setBillStreet(e.target.value)} placeholder="12 rue de la Paix" />
            </Field>
            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <Field label="Code postal">
                <Input value={billZip} onChange={e => setBillZip(e.target.value)} placeholder="75001" />
              </Field>
              <Field label="Ville">
                <Input value={billCity} onChange={e => setBillCity(e.target.value)} placeholder="Paris" />
              </Field>
            </div>
          </div>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>PDF</SectionTitle>
            {/* Logo */}
            <Field label="Logo (affiché en haut des PDFs)">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <>
                    <img src={logoUrl} alt="logo" className="w-14 h-14 rounded-xl object-cover border border-border" />
                    <Button variant="ghost" size="sm" onClick={() => setLogoUrl(null)} className="text-destructive gap-1.5">
                      <X size={14} /> Supprimer
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} className="gap-2">
                    <Upload size={14} /> Choisir une image
                  </Button>
                )}
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoUpload} />
              </div>
            </Field>
            {/* Color picker */}
            <Field label="Couleur principale de l'en-tête">
              <div className="flex items-center gap-3 flex-wrap">
                {INVOICE_COLORS.map(c => (
                  <button key={c} onClick={() => { setInvColor(c); setHexInput(c); }}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${invColor === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} />
                ))}
                <div className="flex items-center gap-1.5">
                  <div className="w-8 h-8 rounded-lg border border-border" style={{ backgroundColor: invColor }} />
                  <Input
                    value={hexInput}
                    onChange={e => { setHexInput(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) setInvColor(e.target.value); }}
                    className="w-28 h-8 text-sm font-mono"
                    placeholder="#2eb8aa"
                    maxLength={7}
                  />
                </div>
              </div>
            </Field>
            {/* Prefixes */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Préfixe factures">
                <Input value={invPrefix} onChange={e => setInvPrefix(e.target.value)} placeholder="FAC" className="font-mono" />
              </Field>
              <Field label="Préfixe devis">
                <Input value={quotePrefix} onChange={e => setQuotePrefix(e.target.value)} placeholder="DEV" className="font-mono" />
              </Field>
            </div>
          </div>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Textes PDF</SectionTitle>
            <Field label="Conditions de paiement par défaut">
              <Textarea value={payTerms} onChange={e => setPayTerms(e.target.value)} rows={3} placeholder="Paiement à réception de facture. Pénalités de retard : 3× le taux légal." className="resize-none" />
            </Field>
            <Field label="Mentions légales (pied de page)">
              <Textarea value={invFooter} onChange={e => setInvFooter(e.target.value)} rows={3} placeholder="Auto-entrepreneur — Dispensé d'immatriculation au RCS. TVA non applicable, art. 293B du CGI." className="resize-none" />
            </Field>
          </div>

          <SaveBar onSave={handleSaveFacturation} saving={updateBilling.isPending || updateProfile.isPending} />
        </TabsContent>

        {/* ── ONGLET 3 — STUDIO ─────────────────────────────────────────── */}
        <TabsContent value="studio" className="pt-4">
          {isAdmin && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-600 font-medium">
              <span>🔑</span> Mode admin — PlanGate désactivé (contenu visible quel que soit le plan)
            </div>
          )}
          <PlanGate
            title={{ fr: 'Fonctionnalités Studio', en: 'Studio Features', es: 'Funciones Studio' }}
            description={{ fr: 'Configurez votre checklist, alertes et catalogue de prestations.', en: 'Configure your checklist, alerts and service catalog.', es: 'Configura tu checklist, alertas y catálogo de servicios.' }}
          >
            <div className="space-y-4">
              {/* Paramètres numériques */}
              <div className="glass-card p-5 space-y-4">
                <SectionTitle>Paramètres session</SectionTitle>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Validité consentement (mois)">
                    <Input type="number" min={1} max={60} value={consentMonths} onChange={e => setConsentMonths(Number(e.target.value))} />
                  </Field>
                  <Field label="Alerte pause (jours)">
                    <Input type="number" min={1} max={30} value={pauseDays} onChange={e => setPauseDays(Number(e.target.value))} />
                  </Field>
                  <Field label="Seuil stock critique">
                    <Input type="number" min={0} max={100} value={stockThresh} onChange={e => setStockThresh(Number(e.target.value))} />
                  </Field>
                </div>
              </div>

              {/* Checklist hygiène */}
              <div className="glass-card p-5 space-y-3">
                <SectionTitle>Checklist hygiène</SectionTitle>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleChecklistDragEnd}>
                  <SortableContext items={checklist.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {checklist.map(item => (
                        <SortableCheckItem key={item.id} item={item} onRemove={removeCheckItem} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <div className="flex gap-2">
                  <Input
                    value={newCheckLabel}
                    onChange={e => setNewCheckLabel(e.target.value)}
                    placeholder="Nouveau point de checklist…"
                    onKeyDown={e => { if (e.key === 'Enter') addCheckItem(); }}
                    className="h-9 text-sm"
                  />
                  <Button size="sm" variant="outline" onClick={addCheckItem} disabled={!newCheckLabel.trim()} className="h-9 gap-1">
                    <Plus size={14} /> Ajouter
                  </Button>
                </div>
              </div>

              {/* Catalogue de prestations */}
              <div className="glass-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <SectionTitle>Catalogue de prestations</SectionTitle>
                  {!newItem.adding && (
                    <Button size="sm" variant="outline" onClick={() => setNewItem(n => ({ ...n, adding: true }))} className="h-7 gap-1 text-xs">
                      <Plus size={12} /> Ajouter
                    </Button>
                  )}
                </div>

                {/* Liste des prestations */}
                <div className="space-y-1">
                  {catalog.map(item => (
                    <div key={item.id} className="rounded-xl border border-border/40 overflow-hidden">
                      {editingItemId === item.id ? (
                        /* ── Mode édition inline ── */
                        <div className="p-3 space-y-2 bg-secondary/20">
                          <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Nom" className="h-8 text-sm" autoFocus />
                          <Input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Description (optionnel)" className="h-8 text-sm" />
                          <div className="grid grid-cols-2 gap-2">
                            <div className="relative">
                              <Input type="number" min={0} step="0.01" value={editForm.default_price}
                                onChange={e => setEditForm(f => ({ ...f, default_price: parseFloat(e.target.value) || 0 }))}
                                className="h-8 text-sm pr-6" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                            </div>
                            <select value={editForm.vat_rate} onChange={e => setEditForm(f => ({ ...f, vat_rate: Number(e.target.value) }))}
                              className="h-8 text-sm rounded-md border border-input bg-background px-2">
                              {VAT_RATES.map(r => <option key={r} value={r}>TVA {r}%</option>)}
                            </select>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingItemId(null)}>Annuler</Button>
                            <Button size="sm" className="h-7 text-xs gap-1" onClick={commitEdit} disabled={saveItem.isPending}>
                              <Check size={12} /> Valider
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* ── Mode lecture ── */
                        <div className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/20 transition-colors group">
                          <div className="flex-1 min-w-0">
                            <button onClick={() => startEdit(item)} className="text-sm font-medium hover:text-primary transition-colors truncate flex items-center gap-1.5">
                              {item.name}
                              <Pencil size={11} className="opacity-0 group-hover:opacity-50" />
                            </button>
                            {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
                          </div>
                          <div className="flex items-center gap-3 shrink-0 text-sm">
                            <span className="font-medium tabular-nums">{item.default_price.toFixed(2)} €</span>
                            <span className="text-xs text-muted-foreground">TVA {item.vat_rate}%</span>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100">
                                  <Trash2 size={14} />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer la prestation ?</AlertDialogTitle>
                                  <AlertDialogDescription>« {item.name} » sera retirée du catalogue.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteItem.mutate(item.id)}>Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {catalog.length === 0 && !newItem.adding && (
                    <p className="text-sm text-muted-foreground text-center py-6">Aucune prestation — cliquez sur Ajouter.</p>
                  )}
                </div>

                {/* Formulaire ajout nouvelle prestation */}
                {newItem.adding && (
                  <div className="rounded-xl border border-primary/30 p-3 space-y-2 bg-primary/5">
                    <Input value={newItem.name} onChange={e => setNewItem(n => ({ ...n, name: e.target.value }))}
                      placeholder="Nom de la prestation *" className="h-8 text-sm" autoFocus />
                    <Input value={newItem.description} onChange={e => setNewItem(n => ({ ...n, description: e.target.value }))}
                      placeholder="Description (optionnel)" className="h-8 text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <Input type="number" min={0} step="0.01" value={newItem.default_price}
                          onChange={e => setNewItem(n => ({ ...n, default_price: parseFloat(e.target.value) || 0 }))}
                          className="h-8 text-sm pr-6" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                      </div>
                      <select value={newItem.vat_rate} onChange={e => setNewItem(n => ({ ...n, vat_rate: Number(e.target.value) }))}
                        className="h-8 text-sm rounded-md border border-input bg-background px-2">
                        {VAT_RATES.map(r => <option key={r} value={r}>TVA {r}%</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setNewItem({ name: '', description: '', default_price: 0, vat_rate: 0, adding: false })}>
                        Annuler
                      </Button>
                      <Button size="sm" className="h-7 text-xs gap-1" onClick={commitNew} disabled={!newItem.name.trim() || saveItem.isPending}>
                        <Check size={12} /> Créer
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <SaveBar onSave={handleSaveStudio} saving={saveStudio.isPending} />
            </div>
          </PlanGate>
        </TabsContent>

        {/* ── ONGLET 4 — SÉCURITÉ ───────────────────────────────────────── */}
        <TabsContent value="securite" className="space-y-4 pt-4">
          <div className="glass-card p-6 space-y-5">
            <SectionTitle>Code PIN client</SectionTitle>
            <label className="flex items-center gap-4 cursor-pointer">
              <div className={`w-14 h-8 rounded-full transition-colors ${pinEnabled ? 'bg-primary' : 'bg-border'}`}
                onClick={() => setPinEnabled(v => !v)}>
                <div className={`w-6 h-6 rounded-full bg-card shadow-md transition-transform mt-1 ${pinEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </div>
              <span className="text-base font-medium">Activer le PIN client</span>
            </label>
            {pinEnabled && (
              <Field label="Code PIN (4–6 chiffres)">
                <Input type="password" inputMode="numeric" maxLength={6} value={pinCode}
                  onChange={e => setPinCode(e.target.value)}
                  className="text-lg tracking-widest w-40" />
              </Field>
            )}
          </div>
          <SaveBar onSave={handleSaveSecurite} saving={updateProfile.isPending} />

          {/* ── Suppression de compte RGPD ── */}
          <div className="glass-card p-6 space-y-4 border border-destructive/20">
            <div>
              <h3 className="font-semibold text-destructive">Zone dangereuse</h3>
              <p className="text-sm text-muted-foreground mt-1">
                La suppression est irréversible. Toutes vos données seront effacées définitivement (RGPD — droit à l'effacement).
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => { setDeleteConfirm(''); setShowDeleteDialog(true); }}
            >
              Supprimer mon compte
            </Button>
          </div>

          {/* Dialog confirmation suppression */}
          {showDeleteDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl">
                <h3 className="font-bold text-lg text-destructive">Supprimer définitivement mon compte</h3>
                <p className="text-sm text-muted-foreground">
                  Cette action est irréversible. Tous vos clients, contrats, factures et données seront supprimés. Votre abonnement sera annulé.
                </p>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Tapez <strong>SUPPRIMER</strong> pour confirmer</label>
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={e => setDeleteConfirm(e.target.value)}
                    placeholder="SUPPRIMER"
                    className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-destructive focus:outline-none"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowDeleteDialog(false)}
                    disabled={deleting}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    disabled={deleteConfirm !== 'SUPPRIMER' || deleting}
                    onClick={handleDeleteAccount}
                  >
                    {deleting ? <Loader2 size={16} className="animate-spin" /> : 'Supprimer'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── ONGLET 5 — ABONNEMENT ─────────────────────────────────────── */}
        <TabsContent value="abonnement" className="space-y-4 pt-4">
          {/* Résumé plan actuel */}
          <div className="glass-card px-5 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <CreditCard size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">
                  Plan <span className="text-primary">{TIERS[tier].name['fr']}</span>
                </p>
                {subscriptionEnd && tier !== 'free' && (
                  <p className="text-xs text-muted-foreground">
                    Renouvellement le {new Date(subscriptionEnd).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
            {tier !== 'free' && (
              <Button variant="outline" size="sm" onClick={() => {
                if (isLegacyFree) {
                  toast.info('Choisissez un plan ci-dessous pour activer votre abonnement.');
                  return;
                }
                manageSubscription();
              }}>
                Gérer
              </Button>
            )}
          </div>

          <ROIBanner />
          <PlanCards
            showToggle
            tier={tier}
            isLegacyFree={isLegacyFree}
            startCheckout={startCheckout}
            manageSubscription={manageSubscription}
            subscriptionEnd={subscriptionEnd}
            subscriptionStatus={subscriptionStatus}
            billingInterval={billingInterval}
            trialEnd={trialEnd}
          />
          <PricingFAQ />
        </TabsContent>
      </Tabs>
    </div>
  );
}
